import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './AgreementModule.css';
import DataTable from './common/DataTable';
import PrintAgreementLetter from './PrintAgreementLetter';
import { buildApiUrl, API_ENDPOINTS } from '../config/api';

/**
 * Unified Enterprise-Grade Agreement Module
 * 
 * Features:
 * - Centralized column management with system defaults
 * - User preferences per view with persistence
 * - Sticky columns and visual hierarchy
 * - Infinite scroll support
 * - Consistent layout
 */
const AgreementModule = ({ user, token, navigateToView }) => {
  const [currentView, setCurrentView] = useState('history');

  // Data state
  const [agreements, setAgreements] = useState([]);
  const [comparisons, setComparisons] = useState([]);
  const [products, setProducts] = useState([]);
  const [summary, setSummary] = useState({
    totalAgreements: 0,
    totalWeightInQuintal: 0,
    totalNumberOfBags: 0,
    totalAmount: 0,
    totalPaldariCost: 0,
    totalPaldariPaid: 0,
    totalPaldariDues: 0
  });
  const [comparisonSummary, setComparisonSummary] = useState({
    totalAgreements: 0,
    totalWeightInQuintal: 0,
    totalNumberOfBags: 0,
    totalWithdrawnBags: 0,
    totalRemainingBags: 0,
    totalAmount: 0,
    totalPaldariCost: 0,
    totalPaldariPaid: 0,
    totalPaldariDues: 0
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [pagination, setPagination] = useState({ total: 0 });
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [selectedAgreementForPrint, setSelectedAgreementForPrint] = useState(null);
  const comparisonContainerRef = useRef(null);
  const comparisonLoadMoreInFlight = useRef(false);

  // Memoized placeholder values to prevent recalculation during re-renders
  const placeholders = useMemo(() => ({
    agreementNumber: "Enter agreement number",
    customerName: "Enter vendor name", 
    fatherName: "Enter father's name",
    mobile: "Enter mobile",
    village: "Enter village"
  }), []);

  // Filter state
  const [filters, setFilters] = useState({
    agreementNumber: '',
    product: '',
    withdrawalStatus: 'all',
    customerName: '',
    fatherName: '',
    mobile: '',
    village: '',
    dateFrom: '',
    dateTo: ''
  });

  // Custom renderers for specific columns
  const customRenderers = {
    agreementNumber: (item) => (
      <div className="cell-content">
        <div className="primary-text">{item.agreementId || item.agreementNumber}</div>
        {item.agreementDate && (
          <div className="secondary-text">
            {new Date(item.agreementDate).toLocaleDateString('en-IN')}
          </div>
        )}
      </div>
    ),
    
    paldariDues: (item) => {
      const paldariDue = item.charges?.handlingChargeDue || 0;
      return (
        <div className="cell-content">
          <div className="primary-text">₹{paldariDue.toLocaleString()}</div>
        </div>
      );
    },
    
    paldariTotal: (item) => {
      const paldariTotal = item.charges?.handlingChargeTotal || 0;
      return (
        <div className="cell-content">
          <div className="primary-text">₹{paldariTotal.toLocaleString()}</div>
        </div>
      );
    },
    
    paldariPaid: (item) => {
      const paldariPaid = item.charges?.handlingChargePaid || 0;
      return (
        <div className="cell-content">
          <div className="primary-text">₹{paldariPaid.toLocaleString()}</div>
        </div>
      );
    },
    
    vendorName: (item) => (
      <div className="cell-content">
        <div className="primary-text">{item.customerName}</div>
        {item.email && (
          <div className="secondary-text">{item.email}</div>
        )}
      </div>
    ),
    
    mobile: (item) => (
      <div className="cell-content">
        <div className="primary-text">{item.mobile}</div>
        {item.altMobile && (
          <div className="secondary-text">{item.altMobile}</div>
        )}
      </div>
    ),
    
    totalAmount: (item) => {
      // Calculate net amount due (total charges - total paid)
      const totalCharges = (item.charges?.handlingChargeTotal || 0) + 
                          (item.charges?.bagChargeTotal || 0) + 
                          (item.charges?.totalStorageCharge || 0);
      const totalPaid = (item.charges?.handlingChargePaid || 0) + 
                       (item.charges?.bagChargePaid || 0) + 
                       (item.charges?.advanceStorageChargesPaid || 0);
      const netAmountDue = totalCharges - totalPaid;
      return (
        <div className="cell-content">
          <div className="primary-text">₹{netAmountDue.toLocaleString()}</div>
        </div>
      );
    },
    
    createdBy: (item) => (
      <div className="cell-content">
        <div className="primary-text">{item.createdBy || '--'}</div>
        {item.createdOn && (
          <div className="secondary-text">
            {new Date(item.createdOn).toLocaleDateString('en-IN')}
          </div>
        )}
      </div>
    ),
    
    status: (item) => {
      // Determine status based on charges paid vs total
      const totalDue = (item.charges?.handlingChargeDue || 0) + 
                      (item.charges?.bagChargeDue || 0) + 
                      (item.charges?.storageChargeDue || 0);
      const status = totalDue > 0 ? 'PENDING' : 'COMPLETED';
      
      return (
        <span
          className="status-badge"
          style={{ backgroundColor: getStatusColor(status) }}
        >
          {status}
        </span>
      );
    },
    
    action: (item) => (
      <div className="action-buttons">
        <button 
          className="btn-action"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleEditAgreement(item);
          }}
          title="Edit Agreement"
        >
          Edit
        </button>
        <button 
          className="btn-action"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleStorageAgreement(item);
          }}
          title="Manage Storage"
        >
          Storage
        </button>
        <button 
          className="btn-action"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handlePrintAgreement(item);
          }}
          title="Print Agreement"
        >
          Print
        </button>
      </div>
    )
  };

  const fetchProducts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(buildApiUrl(API_ENDPOINTS.PRODUCTS), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Failed to load product filters');
      }
      setProducts(result.data.map(product => product.productName));
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Fetch agreements history
  const fetchHistory = useCallback(async (pageNum = 1, reset = false, filterValues = filters) => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      const activeFilters = {};
      if (filterValues.agreementNumber) activeFilters.agreementNumber = filterValues.agreementNumber;
      if (filterValues.product) activeFilters.product = filterValues.product;
      if (filterValues.customerName) activeFilters.customerName = filterValues.customerName;
      if (filterValues.fatherName) activeFilters.fatherName = filterValues.fatherName;
      if (filterValues.mobile) activeFilters.mobile = filterValues.mobile;
      if (filterValues.village) activeFilters.village = filterValues.village;
      if (filterValues.dateFrom) activeFilters.dateFrom = filterValues.dateFrom;
      if (filterValues.dateTo) activeFilters.dateTo = filterValues.dateTo;
      
      const queryParams = new URLSearchParams({
        page: pageNum,
        limit: 100,
        ...activeFilters
      });

      const response = await fetch(`${buildApiUrl(API_ENDPOINTS.AGREEMENTS_HISTORY)}?${queryParams}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch agreement history');

      const data = await response.json();

      if (reset) {
        setAgreements(data.data);
        // Update summary to match backend structure
        setSummary({
          totalAgreements: data.summary.totalAgreements || 0,
          totalWeightInQuintal: data.summary.totalQuintals || 0,
          totalNumberOfBags: data.summary.totalBags || 0,
          totalAmount: data.summary.totalAmount || 0,
          totalPaldariCost: data.summary.totalPaldariCost || 0,
          totalPaldariPaid: data.summary.totalPaldariPaid || 0,
          totalPaldariDues: data.summary.totalPaldariDues || 0
        });
      } else {
        setAgreements(prev => [...prev, ...data.data]);
      }

      setHasMore(data.pagination.hasMore);
      setPage(pageNum);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchComparison = useCallback(async (pageNum = 1, reset = false, filterValues = filters) => {
    setLoading(true);
    setError('');

    try {
      const activeFilters = {};
      Object.entries(filterValues).forEach(([key, value]) => {
        if (value) activeFilters[key] = value;
      });

      const queryParams = new URLSearchParams({
        page: pageNum,
        limit: 100,
        ...activeFilters
      });
      const response = await fetch(
        `${buildApiUrl(API_ENDPOINTS.AGREEMENTS_WITHDRAWAL_COMPARISON)}?${queryParams}`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to fetch agreement versus withdrawal comparison');
      }

      let totalWithdrawnBags = result.summary?.totalWithdrawnBags;
      let totalRemainingBags = result.summary?.totalRemainingBags;
      if (!Number.isFinite(totalWithdrawnBags) || !Number.isFinite(totalRemainingBags)) {
        const allRowsParams = new URLSearchParams({
          page: 1,
          limit: 50000,
          ...activeFilters
        });
        const allRowsResponse = await fetch(
          `${buildApiUrl(API_ENDPOINTS.AGREEMENTS_WITHDRAWAL_COMPARISON)}?${allRowsParams}`,
          { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
        );
        const allRowsResult = await allRowsResponse.json();
        if (!allRowsResponse.ok) {
          throw new Error(allRowsResult.error || 'Failed to fetch agreement withdrawal totals');
        }

        totalWithdrawnBags = allRowsResult.data.reduce(
          (total, agreement) => total + (agreement.totalBagsWithdrawn || 0),
          0
        );
        totalRemainingBags = allRowsResult.data.reduce(
          (total, agreement) => total + (
            agreement.bagsRemaining ?? (agreement.numberOfBags || 0) - (agreement.totalBagsWithdrawn || 0)
          ),
          0
        );
      }

      setComparisons(prev => reset ? result.data : [...prev, ...result.data]);
      setComparisonSummary({
        totalAgreements: result.summary?.totalAgreements || 0,
        totalWeightInQuintal: result.summary?.totalQuintals || 0,
        totalNumberOfBags: result.summary?.totalBags || 0,
        totalWithdrawnBags,
        totalRemainingBags,
        totalAmount: result.summary?.totalAmount || 0,
        totalPaldariCost: result.summary?.totalPaldariCost || 0,
        totalPaldariPaid: result.summary?.totalPaldariPaid || 0,
        totalPaldariDues: result.summary?.totalPaldariDues || 0
      });
      setPagination(result.pagination);
      setPage(pageNum);
      setHasMore(result.pagination.hasMore);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Apply filters
  const handleApplyFilters = () => {
    setPage(1);
    if (currentView === 'history') {
      setAgreements([]);
      fetchHistory(1, true);
    } else {
      setComparisons([]);
      fetchComparison(1, true);
    }
  };

  // Reset filters
  const handleResetFilters = () => {
    const emptyFilters = {
      agreementNumber: '',
      product: '',
      withdrawalStatus: 'all',
      customerName: '',
      fatherName: '',
      mobile: '',
      village: '',
      dateFrom: '',
      dateTo: ''
    };
    setFilters(emptyFilters);
    
    setPage(1);
    if (currentView === 'history') {
      setAgreements([]);
      fetchHistory(1, true, emptyFilters);
    } else {
      setComparisons([]);
      fetchComparison(1, true, emptyFilters);
    }
  };

  // Handle infinite scroll
  const handleLoadMore = () => {
    if (currentView === 'history') {
      fetchHistory(page + 1, false);
    } else {
      fetchComparison(page + 1, false);
    }
  };

  const handleComparisonScroll = () => {
    const container = comparisonContainerRef.current;
    if (
      !container ||
      currentView !== 'comparison' ||
      !hasMore ||
      loading ||
      comparisonLoadMoreInFlight.current
    ) {
      return;
    }

    if (container.scrollHeight - container.scrollTop <= container.clientHeight + 100) {
      comparisonLoadMoreInFlight.current = true;
      fetchComparison(page + 1, false).finally(() => {
        comparisonLoadMoreInFlight.current = false;
      });
    }
  };

  const handleViewChange = (view) => {
    if (view === currentView) return;

    setCurrentView(view);
    setPage(1);
    if (view === 'history') {
      setAgreements([]);
      fetchHistory(1, true);
    } else {
      setComparisons([]);
      fetchComparison(1, true);
    }
  };

  // Handle edit agreement
  const handleEditAgreement = (agreement) => {
    console.log('✏️ Navigating to Edit Agreement:', agreement.agreementId);
    
    if (navigateToView) {
      navigateToView('edit-agreement', {
        agreementId: agreement._id, // Use MongoDB ObjectId for navigation
        agreementNumber: agreement.agreementId,
        agreementData: agreement
      });
    }
  };

  // Handle storage agreement
  const handleStorageAgreement = (agreement) => {
    console.log('📦 Navigating to Agreement Storage:', agreement.agreementId);
    
    if (navigateToView) {
      navigateToView('agreement-storage', {
        agreementId: agreement._id, // Use MongoDB ObjectId for navigation
        agreementNumber: agreement.agreementId,
        agreementData: agreement
      });
    }
  };

  // Handle print agreement
  const handlePrintAgreement = async (agreement) => {
    console.log('🖨️ Print Agreement Letter called:', agreement);
    console.log('Agreement ID:', agreement.agreementId);
    console.log('Agreement _id:', agreement._id);
    
    if (!agreement._id) {
      console.error('No _id found in agreement object:', agreement);
      alert('Error: Agreement ID not found');
      return;
    }

    try {
      console.log('Fetching agreement details from API...');
      
      // Fetch full agreement details for printing
      const response = await fetch(`${buildApiUrl(API_ENDPOINTS.AGREEMENTS)}/${agreement._id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('API Response status:', response.status);
      console.log('API Response ok:', response.ok);

      if (response.ok) {
        const result = await response.json();
        console.log('Agreement data fetched for print:', result.data);
        
        if (result.success && result.data) {
          setSelectedAgreementForPrint(result.data);
          setShowPrintModal(true);
          console.log('Print modal should now be visible');
        } else {
          console.error('Invalid API response structure:', result);
          alert('Error: Invalid response from server');
        }
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Failed to fetch agreement details:', errorData);
        alert(`Failed to fetch agreement details: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error fetching agreement for print:', error);
      alert(`Error loading agreement details: ${error.message}`);
    }
  };

  const closePrintModal = () => {
    console.log('Closing print modal');
    setShowPrintModal(false);
    setSelectedAgreementForPrint(null);
  };

  // Handle export to PDF
  const handleExportPDF = async () => {
    if (exporting) return;
    setExporting(true);

    if (currentView === 'comparison') {
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        setExporting(false);
        alert('Please allow pop-ups to export PDF');
        return;
      }

      try {
        const activeFilters = Object.fromEntries(
          Object.entries(filters).filter(([key, value]) => (
            value.trim() && !(key === 'withdrawalStatus' && value === 'all')
          ))
        );
        const queryParams = new URLSearchParams({
          page: 1,
          limit: 50000,
          ...activeFilters
        });
        const response = await fetch(
          `${buildApiUrl(API_ENDPOINTS.AGREEMENTS_WITHDRAWAL_COMPARISON)}?${queryParams}`,
          { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
        );
        const result = await response.json();
        if (!response.ok) {
          throw new Error(result.error || 'Failed to fetch comparison data for export');
        }
        if (!result.data.length) {
          printWindow.close();
          alert('No data to export');
          return;
        }

        const escapeHtml = (value) => String(value ?? '--').replace(/[&<>"']/g, character => ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;'
        })[character]);
        const formatDate = (value) => {
          if (!value) return '--';
          const date = new Date(value);
          return Number.isNaN(date.getTime()) ? '--' : date.toLocaleDateString('en-IN');
        };
        const rows = result.data.map(agreement => {
          const agreedBags = agreement.numberOfBags || 0;
          const withdrawnBags = agreement.totalBagsWithdrawn || 0;
          const remainingBags = agreement.bagsRemaining ?? agreedBags;
          const status = withdrawnBags === 0
            ? 'Remaining'
            : remainingBags <= 0
              ? 'Withdrawn'
              : 'Partial';

          return `
            <tr>
              <td>${escapeHtml(agreement.agreementId)}</td>
              <td>${escapeHtml(agreement.lotNumber)}</td>
              <td>${escapeHtml(agreement.customerName)}</td>
              <td>${escapeHtml(formatDate(agreement.agreementDate))}</td>
              <td>${agreedBags.toLocaleString()}</td>
              <td>${withdrawnBags.toLocaleString()}</td>
              <td>${remainingBags.toLocaleString()}</td>
              <td>${agreement.withdrawalCount || 0}</td>
              <td>${status}</td>
            </tr>`;
        }).join('');
        const filterSummary = Object.entries(activeFilters)
          .map(([key, value]) => `<li>${escapeHtml(key)}: ${escapeHtml(value)}</li>`)
          .join('');
        const withdrawnSummary = Number.isFinite(result.summary?.totalWithdrawnBags)
          ? result.summary.totalWithdrawnBags
          : result.data.reduce((total, agreement) => total + (agreement.totalBagsWithdrawn || 0), 0);
        const remainingSummary = Number.isFinite(result.summary?.totalRemainingBags)
          ? result.summary.totalRemainingBags
          : result.data.reduce((total, agreement) => total + (
            agreement.bagsRemaining ?? (agreement.numberOfBags || 0) - (agreement.totalBagsWithdrawn || 0)
          ), 0);
        const formatSummaryNumber = (value) => Number(value || 0).toLocaleString('en-IN');
        const exportSummaryCards = [
          ['Total Agreements', formatSummaryNumber(result.summary?.totalAgreements ?? result.data.length)],
          ['Total Weight', `${formatSummaryNumber(result.summary?.totalQuintals)} Q`],
          ['Total Bags', formatSummaryNumber(result.summary?.totalBags)],
          ['Total Withdrawn', formatSummaryNumber(withdrawnSummary)],
          ['Total Remaining', formatSummaryNumber(remainingSummary)],
          ['Total Paldari', `₹${formatSummaryNumber(result.summary?.totalPaldariCost)}`],
          ['Paldari Paid', `₹${formatSummaryNumber(result.summary?.totalPaldariPaid)}`],
          ['Paldari Due', `₹${formatSummaryNumber(result.summary?.totalPaldariDues)}`],
          ['Total Amount Due', `₹${formatSummaryNumber(result.summary?.totalAmount)}`]
        ];
        const summaryCards = exportSummaryCards.map(([label, value]) => `
          <div class="summary-card">
            <div class="summary-label">${escapeHtml(label)}</div>
            <div class="summary-value">${escapeHtml(value)}</div>
          </div>
        `).join('');

        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <meta charset="UTF-8">
              <title>Agreement vs Withdrawal Report</title>
              <style>
                body { font-family: Arial, sans-serif; padding: 20px; color: #222; }
                h1, .report-meta { text-align: center; }
                .filters { margin: 20px 0; }
                .summary-section {
                  display: grid;
                  grid-template-columns: repeat(4, minmax(0, 1fr));
                  gap: 10px;
                  margin: 20px 0;
                }
                .summary-card {
                  border: 1px solid #ccc;
                  border-radius: 5px;
                  padding: 10px;
                  text-align: center;
                }
                .summary-label { color: #666; font-size: 10px; margin-bottom: 5px; }
                .summary-value { font-size: 15px; font-weight: bold; }
                table { width: 100%; border-collapse: collapse; font-size: 11px; }
                th, td { border: 1px solid #ccc; padding: 7px; text-align: left; }
                th { background: #f1f1f1; }
                tr { page-break-inside: avoid; }
                .print-action { text-align: center; margin: 20px; }
                .print-action button { padding: 10px 20px; cursor: pointer; }
                @media (max-width: 700px) {
                  .summary-section { grid-template-columns: repeat(2, minmax(0, 1fr)); }
                }
                @media print { .print-action { display: none; } }
              </style>
            </head>
            <body>
              <h1>Agreement vs Withdrawal Report</h1>
              <p class="report-meta">Generated on: ${escapeHtml(new Date().toLocaleString('en-IN'))}</p>
              <p class="report-meta">Total records: ${result.data.length}</p>
              <section class="summary-section">${summaryCards}</section>
              ${filterSummary
                ? `<section class="filters"><strong>Filters applied:</strong><ul>${filterSummary}</ul></section>`
                : '<p>Filters applied: None</p>'}
              <table>
                <thead>
                  <tr>
                    <th>Agreement No.</th><th>Lot</th><th>Vendor</th><th>Agreement Date</th>
                    <th>Agreed Bags</th><th>Bags Withdrawn</th><th>Bags Remaining</th>
                    <th>Withdrawals</th><th>Status</th>
                  </tr>
                </thead>
                <tbody>${rows}</tbody>
              </table>
              <div class="print-action"><button onclick="window.print()">Print / Save as PDF</button></div>
            </body>
          </html>
        `);
        printWindow.document.close();
      } catch (error) {
        printWindow.close();
        console.error('Error exporting agreement versus withdrawal report:', error);
        alert(`Failed to export PDF: ${error.message}`);
      } finally {
        setExporting(false);
      }
      return;
    }

    try {
      // Fetch ALL records matching current filters (not just paginated view)
      const token = localStorage.getItem('token');
      
      const activeFilters = {};
      if (filters.agreementNumber) activeFilters.agreementNumber = filters.agreementNumber;
      if (filters.product) activeFilters.product = filters.product;
      if (filters.customerName) activeFilters.customerName = filters.customerName;
      if (filters.mobile) activeFilters.mobile = filters.mobile;
      if (filters.village) activeFilters.village = filters.village;
      if (filters.dateFrom) activeFilters.dateFrom = filters.dateFrom;
      if (filters.dateTo) activeFilters.dateTo = filters.dateTo;
      
      // Fetch with a very high limit to get all records
      const queryParams = new URLSearchParams({
        page: 1,
        limit: 50000, // High limit to get all records
        ...activeFilters
      });

      const response = await fetch(`${buildApiUrl(API_ENDPOINTS.AGREEMENTS_HISTORY)}?${queryParams}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch agreement data for export');

      const data = await response.json();
      const allAgreements = data.data;
      const exportSummary = {
        totalAgreements: data.summary.totalAgreements || 0,
        totalWeightInQuintal: data.summary.totalQuintals || 0,
        totalNumberOfBags: data.summary.totalBags || 0,
        totalAmount: data.summary.totalAmount || 0,
        totalPaldariCost: data.summary.totalPaldariCost || 0,
        totalPaldariPaid: data.summary.totalPaldariPaid || 0,
        totalPaldariDues: data.summary.totalPaldariDues || 0
      };

      if (allAgreements.length === 0) {
        alert('No data to export');
        return;
      }

      console.log(`📄 Exporting ${allAgreements.length} agreements to PDF`);

      // Create a printable HTML document
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('Please allow pop-ups to export PDF');
        return;
      }

      // Generate HTML content
      const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Agreement History Report</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
            font-size: 12px;
          }
          
          .report-header {
            text-align: center;
            margin-bottom: 20px;
            border-bottom: 2px solid #333;
            padding-bottom: 10px;
          }
          
          .report-header h1 {
            font-size: 20px;
            margin-bottom: 5px;
          }
          
          .report-header .date {
            font-size: 11px;
            color: #666;
          }
          
          .summary-section {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 10px;
            margin-bottom: 20px;
            padding: 15px;
            background: #f5f5f5;
            border-radius: 5px;
          }
          
          .summary-item {
            text-align: center;
          }
          
          .summary-label {
            font-size: 10px;
            color: #666;
            margin-bottom: 3px;
          }
          
          .summary-value {
            font-size: 14px;
            font-weight: bold;
            color: #333;
          }
          
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
          }
          
          th, td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
          }
          
          th {
            background-color: #4CAF50;
            color: white;
            font-weight: bold;
            font-size: 11px;
          }
          
          td {
            font-size: 10px;
          }
          
          tr:nth-child(even) {
            background-color: #f9f9f9;
          }
          
          .text-right {
            text-align: right;
          }
          
          .text-center {
            text-align: center;
          }
          
          .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 10px;
            color: #666;
            border-top: 1px solid #ddd;
            padding-top: 10px;
          }
          
          @media print {
            body {
              padding: 10px;
            }
            
            .no-print {
              display: none;
            }
          }
        </style>
      </head>
      <body>
        <div class="report-header">
          <h1>Agreement History Report</h1>
          <div class="date">Generated on: ${new Date().toLocaleString('en-IN')}</div>
        </div>
        
        <div class="summary-section">
          <div class="summary-item">
            <div class="summary-label">Total Agreements</div>
            <div class="summary-value">${exportSummary.totalAgreements}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Total Weight</div>
            <div class="summary-value">${exportSummary.totalWeightInQuintal} Q</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Total Bags</div>
            <div class="summary-value">${exportSummary.totalNumberOfBags}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Total Paldari</div>
            <div class="summary-value">₹${exportSummary.totalPaldariCost?.toLocaleString()}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Paldari Paid</div>
            <div class="summary-value">₹${exportSummary.totalPaldariPaid?.toLocaleString()}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Paldari Due</div>
            <div class="summary-value">₹${exportSummary.totalPaldariDues?.toLocaleString()}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Total Amount Due</div>
            <div class="summary-value">₹${exportSummary.totalAmount?.toLocaleString()}</div>
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>S.No</th>
              <th>Agreement No</th>
              <th>Date</th>
              <th>Lot Number</th>
              <th>Customer Name</th>
              <th>Mobile</th>
              <th>Village</th>
              <th class="text-right">Weight (Q)</th>
              <th class="text-right">Bags</th>
              <th class="text-right">Paldari Total</th>
              <th class="text-right">Paldari Paid</th>
              <th class="text-right">Paldari Due</th>
              <th class="text-right">Amount Due</th>
            </tr>
          </thead>
          <tbody>
            ${allAgreements.map((item, index) => {
              const totalCharges = (item.charges?.handlingChargeTotal || 0) + 
                                  (item.charges?.bagChargeTotal || 0) + 
                                  (item.charges?.totalStorageCharge || 0);
              const totalPaid = (item.charges?.handlingChargePaid || 0) + 
                               (item.charges?.bagChargePaid || 0) + 
                               (item.charges?.advanceStorageChargesPaid || 0);
              const netAmountDue = totalCharges - totalPaid;
              
              return `
                <tr>
                  <td class="text-center">${index + 1}</td>
                  <td>${item.agreementId || '--'}</td>
                  <td>${item.agreementDate ? new Date(item.agreementDate).toLocaleDateString('en-IN') : '--'}</td>
                  <td>${item.lotNumber || '--'}</td>
                  <td>${item.customerName || '--'}</td>
                  <td>${item.mobile || '--'}</td>
                  <td>${item.addressLine1 || '--'}</td>
                  <td class="text-right">${item.weightInQuintal || 0}</td>
                  <td class="text-right">${item.numberOfBags || 0}</td>
                  <td class="text-right">₹${(item.charges?.handlingChargeTotal || 0).toLocaleString()}</td>
                  <td class="text-right">₹${(item.charges?.handlingChargePaid || 0).toLocaleString()}</td>
                  <td class="text-right">₹${(item.charges?.handlingChargeDue || 0).toLocaleString()}</td>
                  <td class="text-right">₹${netAmountDue.toLocaleString()}</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
        
        <div class="footer">
          <p>This is a computer-generated report. Total records: ${allAgreements.length}</p>
          <p>Filters Applied: ${Object.entries(filters).filter(([key, value]) => key !== 'withdrawalStatus' && value).map(([key, value]) => `${key}: ${value}`).join(', ') || 'None'}</p>
        </div>
        
        <div class="no-print" style="text-align: center; margin-top: 20px;">
          <button onclick="window.print()" style="padding: 10px 20px; font-size: 14px; cursor: pointer; background: #4CAF50; color: white; border: none; border-radius: 4px;">
            Print / Save as PDF
          </button>
          <button onclick="window.close()" style="padding: 10px 20px; font-size: 14px; cursor: pointer; background: #f44336; color: white; border: none; border-radius: 4px; margin-left: 10px;">
            Close
          </button>
        </div>
      </body>
      </html>
      `;

      printWindow.document.write(htmlContent);
      printWindow.document.close();
    
    } catch (error) {
      console.error('❌ Error exporting PDF:', error);
      alert(`Failed to export PDF: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED': return '#28a745';
      case 'PENDING': return '#ffc107';
      case 'ACTIVE': return '#007bff';
      case 'CANCELLED': return '#dc3545';
      default: return '#6c757d';
    }
  };

  // Initial load
  useEffect(() => {
    fetchHistory(1, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const displayedSummary = currentView === 'history' ? summary : comparisonSummary;

  return (
    <div className="agreement-module">
      {/* Header */}
      <div className="module-header">
        <div className="header-content">
          <h1>Agreement Management</h1>
          <button 
            className="btn-export-pdf"
            onClick={handleExportPDF}
            disabled={exporting || (currentView === 'history' ? agreements.length === 0 : comparisons.length === 0)}
            title={currentView === 'history' ? 'Export Agreement History to PDF' : 'Export Agreement vs Withdrawal to PDF'}
          >
            {exporting ? '⏳ Fetching all data...' : '📄 Export PDF'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          ✗ {error}
        </div>
      )}

      <div className="agreement-view-tabs" role="tablist" aria-label="Agreement history views">
        <button
          type="button"
          role="tab"
          aria-selected={currentView === 'history'}
          className={currentView === 'history' ? 'active' : ''}
          onClick={() => handleViewChange('history')}
        >
          📋 Agreement History
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={currentView === 'comparison'}
          className={currentView === 'comparison' ? 'active' : ''}
          onClick={() => handleViewChange('comparison')}
        >
          📊 Agreement vs Withdrawal
        </button>
      </div>

      {/* Filters */}
      <div className="filter-section">
        <div className="filter-grid">
          <div className="filter-group">
            <label>Agreement Number</label>
            <input
              type="text"
              name="agreementNumber"
              value={filters.agreementNumber}
              onChange={handleFilterChange}
              placeholder={placeholders.agreementNumber}
            />
          </div>
          <div className="filter-group">
            <label htmlFor="agreement-product-filter">Product Name</label>
            <select
              id="agreement-product-filter"
              name="product"
              value={filters.product}
              onChange={handleFilterChange}
            >
              <option value="">All Products</option>
              {products.map(product => (
                <option key={product} value={product}>{product}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Vendor Name</label>
            <input
              type="text"
              name="customerName"
              value={filters.customerName}
              onChange={handleFilterChange}
              placeholder={placeholders.customerName}
            />
          </div>
          <div className="filter-group">
            <label>Father's Name</label>
            <input
              type="text"
              name="fatherName"
              value={filters.fatherName}
              onChange={handleFilterChange}
              placeholder={placeholders.fatherName}
            />
          </div>
          <div className="filter-group">
            <label>Mobile</label>
            <input
              type="text"
              name="mobile"
              value={filters.mobile}
              onChange={handleFilterChange}
              placeholder={placeholders.mobile}
              maxLength="10"
            />
          </div>
          <div className="filter-group">
            <label>Village</label>
            <input
              type="text"
              name="village"
              value={filters.village}
              onChange={handleFilterChange}
              placeholder={placeholders.village}
            />
          </div>
          <div className="filter-group">
            <label>Date From</label>
            <input
              type="date"
              name="dateFrom"
              value={filters.dateFrom}
              onChange={handleFilterChange}
            />
          </div>
          <div className="filter-group">
            <label>Date To</label>
            <input
              type="date"
              name="dateTo"
              value={filters.dateTo}
              onChange={handleFilterChange}
            />
          </div>
          {currentView === 'comparison' && (
            <div className="filter-group">
              <label htmlFor="withdrawal-status-filter">Status</label>
              <select
                id="withdrawal-status-filter"
                name="withdrawalStatus"
                value={filters.withdrawalStatus}
                onChange={handleFilterChange}
              >
                <option value="all">All</option>
                <option value="withdrawn">Withdrawn</option>
                <option value="remaining">Remaining</option>
                <option value="partial">Partial</option>
              </select>
            </div>
          )}
        </div>
        <div className="filter-actions">
          <button className="btn-apply" onClick={handleApplyFilters}>
            Apply Filters
          </button>
          <button className="btn-reset" onClick={handleResetFilters}>
            Reset
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="summary-section">
        <div className="summary-card">
          <div className="summary-label">Total Agreements</div>
          <div className="summary-value">{displayedSummary.totalAgreements}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Total Weight</div>
          <div className="summary-value">{displayedSummary.totalWeightInQuintal} Q</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Total Bags</div>
          <div className="summary-value">{displayedSummary.totalNumberOfBags}</div>
        </div>
        {currentView === 'comparison' && (
          <>
            <div className="summary-card">
              <div className="summary-label">Total Withdrawn</div>
              <div className="summary-value">{displayedSummary.totalWithdrawnBags.toLocaleString('en-IN')}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Total Remaining</div>
              <div className="summary-value">{displayedSummary.totalRemainingBags.toLocaleString('en-IN')}</div>
            </div>
          </>
        )}
        <div className="summary-card">
          <div className="summary-label">Total Paldari</div>
          <div className="summary-value">₹{displayedSummary.totalPaldariCost?.toLocaleString()}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Paldari Paid</div>
          <div className="summary-value">₹{displayedSummary.totalPaldariPaid?.toLocaleString()}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Paldari Due</div>
          <div className="summary-value">₹{displayedSummary.totalPaldariDues?.toLocaleString()}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Total Amount Due</div>
          <div className="summary-value">₹{displayedSummary.totalAmount?.toLocaleString()}</div>
        </div>
      </div>

      {/* Data Table with Column Management */}
      {currentView === 'history' ? (
        <DataTable
          data={agreements}
          viewKey="agreement-history"
          userId={user?.id}
          loading={loading}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
          customRenderers={customRenderers}
          className="agreement-table"
          maxHeight="600px"
        />
      ) : (
        <section className="agreement-comparison-section">
          <h2>Agreement vs Withdrawal ({pagination.total} total)</h2>
          <div
            className="agreement-comparison-table-container"
            ref={comparisonContainerRef}
            onScroll={handleComparisonScroll}
          >
            <table className="agreement-comparison-table">
              <thead>
                <tr>
                  <th>Agreement No.</th>
                  <th>Lot</th>
                  <th>Vendor</th>
                  <th>Agreement Date</th>
                  <th>Agreed Bags</th>
                  <th>Bags Withdrawn</th>
                  <th>Bags Remaining</th>
                  <th>Withdrawals</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {comparisons.map(agreement => {
                  const agreedBags = agreement.numberOfBags || 0;
                  const withdrawnBags = agreement.totalBagsWithdrawn || 0;
                  const remainingBags = agreement.bagsRemaining ?? agreedBags;
                  const status = withdrawnBags === 0
                    ? 'Remaining'
                    : remainingBags <= 0
                      ? 'Withdrawn'
                      : 'Partial';

                  return (
                    <tr key={agreement._id}>
                      <td>{agreement.agreementId || '--'}</td>
                      <td>{agreement.lotNumber || '--'}</td>
                      <td>{agreement.customerName || '--'}</td>
                      <td>{agreement.agreementDate ? new Date(agreement.agreementDate).toLocaleDateString('en-IN') : '--'}</td>
                      <td>{agreedBags.toLocaleString()}</td>
                      <td>{withdrawnBags.toLocaleString()}</td>
                      <td>{remainingBags.toLocaleString()}</td>
                      <td>{agreement.withdrawalCount || 0}</td>
                      <td>
                        <span className={`agreement-comparison-status ${status.toLowerCase()}`}>
                          {status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {loading && comparisons.length === 0 && (
                  <tr>
                    <td colSpan="9" className="agreement-comparison-empty">Loading agreements...</td>
                  </tr>
                )}
                {loading && comparisons.length > 0 && (
                  <tr>
                    <td colSpan="9" className="agreement-comparison-empty">Loading more agreements...</td>
                  </tr>
                )}
                {!loading && comparisons.length === 0 && (
                  <tr>
                    <td colSpan="9" className="agreement-comparison-empty">No agreements found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Print Modal */}
      {showPrintModal && selectedAgreementForPrint && (
        <PrintAgreementLetter 
          agreement={selectedAgreementForPrint}
          user={user}
          onClose={closePrintModal}
        />
      )}
      
      {/* Debug info */}
      {showPrintModal && !selectedAgreementForPrint && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'white',
          padding: '20px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          zIndex: 9999
        }}>
          <p>Loading agreement data for printing...</p>
          <button onClick={closePrintModal}>Close</button>
        </div>
      )}
    </div>
  );
};

export default AgreementModule;